import { Service } from 'typedi';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Appl } from '../../../../core/Appl';
import { BaseViewModel } from '../../../../core/services/BaseViewModel';
import { ICaseStatusService } from '../CaseStatusService';
import { MemberCaseStatusDto, PipelineType } from './CaseStatusModel';

@Service() // eslint-disable-next-line
export default class CaseStatusViewModel extends BaseViewModel {
  private service: ICaseStatusService;

  @observable public pin: string = '';
  @observable public dateOfBirth: Date | undefined = undefined;
  @observable public pipelineType: PipelineType | '' = '';
  @observable public caseList: MemberCaseStatusDto[] = [];
  @observable public isLoading: boolean = false;
  @observable public errorMessage: string = '';
  @observable public hasSearched: boolean = false;

  constructor() {
    super();
    this.service = Appl.Services.get<ICaseStatusService>('ICaseStatusService');
    makeObservable(this);
  }

  @action
  public async lookupStatusAsync(): Promise<void> {
    if (!this.pin || !this.dateOfBirth || !(this.dateOfBirth instanceof Date)) {
      runInAction(() => {
        this.errorMessage = 'Please enter your Member ID and Date of Birth.';
      });
      return;
    }

    runInAction(() => {
      this.isLoading = true;
      this.errorMessage = '';
      this.caseList = [];
      this.hasSearched = false;
    });

    const dob = this.dateOfBirth!;
    const dobStr = `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, '0')}-${String(dob.getDate()).padStart(2, '0')}`;
    const response = await this.service.lookupCaseStatusAsync({
      pin: this.pin,
      dateOfBirth: dobStr,
      pipelineType: this.pipelineType || undefined,
    });

    runInAction(() => {
      this.isLoading = false;
      this.hasSearched = true;
      if (response.success && Array.isArray(response.data) && response.data.length > 0) {
        this.caseList = response.data as MemberCaseStatusDto[];
      } else if (response.error?.status === 404 || (response.success && response.data?.length === 0)) {
        this.errorMessage = 'No active case found. Please verify your Member ID and Date of Birth.';
      } else {
        this.errorMessage = 'Unable to retrieve your case status. Please try again or contact SCERS.';
      }
    });
  }

  @action
  public reset(): void {
    this.pin = '';
    this.dateOfBirth = undefined;
    this.pipelineType = '';
    this.caseList = [];
    this.errorMessage = '';
    this.hasSearched = false;
  }
}
